import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Grid,
  Typography,
  LinearProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import {
  DocumentFoldersClient,
  DocumentFolderDto,
  UpdateFolderRequest,
} from '../../app/services/api/documentsClient';
import { DocumentCategory } from '../../enums/DocumentPermission';

const foldersClient = new DocumentFoldersClient();

const FolderEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const folderId = Number(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [folder, setFolder] = useState<DocumentFolderDto | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<DocumentCategory>(DocumentCategory.General);

  useEffect(() => {
    const loadFolder = async () => {
      try {
        const folderData = await foldersClient.getFolder(folderId);
        setFolder(folderData);
        setName(folderData.name);
        setDescription(folderData.description ?? '');
        setCategory(folderData.category);
      } catch (error) {
        console.error('Error loading folder:', error);
      } finally {
        setLoading(false);
      }
    };

    if (folderId) {
      loadFolder();
    }
  }, [folderId]);

  const handleSave = async () => {
    if (!name.trim()) return;

    setSaving(true);
    try {
      const request: UpdateFolderRequest = {
        name: name.trim(),
        description: description.trim() || null,
        category,
      };
      await foldersClient.updateFolder(folderId, request);

      // Navigate back to the parent folder or documents root
      if (folder?.parentFolderId) {
        navigate(`/documents?folderId=${folder.parentFolderId}`);
      } else {
        navigate('/documents');
      }
    } catch (error) {
      console.error('Error updating folder:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (folder?.parentFolderId) {
      navigate(`/documents?folderId=${folder.parentFolderId}`);
    } else {
      navigate('/documents');
    }
  };

  if (loading) {
    return (
      <PageArea>
        <PageTitle>Edit Folder</PageTitle>
        <LinearProgress />
      </PageArea>
    );
  }

  if (!folder) {
    return (
      <PageArea>
        <PageTitle>Edit Folder</PageTitle>
        <Section>
          <Box sx={{ p: 3 }}>
            <Typography color="error">Folder not found</Typography>
            <Button
              variant="secondary"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/documents')}
              sx={{ mt: 2 }}
            >
              Back to Documents
            </Button>
          </Box>
        </Section>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <PageTitle subTitle={`Editing folder in ${folder.path.slice(0, -1).join(' / ') || 'root'}`}>
        Edit Folder
      </PageTitle>

      <Box sx={{ mt: 3 }}>
        <Section>
          <Box sx={{ p: 3 }}>
            <Grid
              container
              spacing={3}
            >
              <Grid
                item
                xs={12}
              >
                <TextField
                  fullWidth
                  label="Folder Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  error={!name.trim()}
                  helperText={!name.trim() ? 'Folder name is required' : ''}
                />
              </Grid>
              <Grid
                item
                xs={12}
              >
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  select
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as DocumentCategory)}
                >
                  {Object.values(DocumentCategory)
                    .filter(c => c !== DocumentCategory.Evidence)
                    .map(cat => (
                      <MenuItem
                        key={cat}
                        value={cat}
                      >
                        {cat}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label="Scope"
                  value={folder.scope}
                  disabled
                  helperText="Scope cannot be changed after folder creation"
                />
              </Grid>
              <Grid
                item
                xs={12}
              >
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={!name.trim() || saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Section>
      </Box>
    </PageArea>
  );
};

export default FolderEdit;
